import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Wand2, Pencil, Hand } from "lucide-react";

/**
 * Shared Vendor Parameters picker used by Campaigns, Optimizer and
 * Budget Sensitivity forms.
 *
 * Props:
 *   vendors          – array of vendor records
 *   selectedId       – currently selected vendor id ("" for none)
 *   sourceType       – "manual" | "vendor" | "custom"
 *   onChange(id, v)  – called when the user picks a vendor (v = full record
 *                       or null for "None — Manual Input")
 *   testId           – data-testid prefix for the trigger
 *   label            – field label (default "Vendor Parameters")
 *   disabled         – disable the select
 */
export default function VendorParametersSelect({
  vendors,
  selectedId,
  sourceType,
  onChange,
  testId = "vp-select",
  label = "Vendor Parameters",
  disabled = false,
}) {
  const handle = (val) => {
    if (val === "__none__") {
      onChange("", null);
      return;
    }
    const v = vendors.find((x) => x.id === val) || null;
    onChange(val, v);
  };

  const helper =
    sourceType === "custom"
      ? "Parameters have been manually adjusted from the selected vendor."
      : sourceType === "vendor"
        ? "Values are auto-filled from the selected Vendor Parameters and can still be edited."
        : "Optional. Leave empty to use manual input values.";

  return (
    <div data-testid={`${testId}-wrap`}>
      <div className="flex items-center justify-between gap-2">
        <label className="text-xs font-medium uppercase tracking-wider text-slate-500">{label}</label>
        <SourceBadge sourceType={sourceType} />
      </div>
      <Select value={selectedId || "__none__"} onValueChange={handle} disabled={disabled}>
        <SelectTrigger className="mt-2" data-testid={testId}>
          <SelectValue placeholder="None — Manual Input" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="__none__">None — Manual Input</SelectItem>
          {vendors.map((v) => (
            <SelectItem key={v.id} value={v.id}>
              {v.vendor_name}
              {v.target_audience ? ` · ${v.target_audience}` : ""}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <div className={`text-[11px] mt-1 ${sourceType === "custom" ? "text-amber-700" : "text-slate-500"}`}>
        {helper}
      </div>
    </div>
  );
}

function SourceBadge({ sourceType }) {
  if (sourceType === "vendor") {
    return (
      <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-full bg-[#0A3D91]/10 text-[#0A3D91]" data-testid="vp-source-badge">
        <Wand2 className="h-3 w-3" /> Vendor Parameters
      </span>
    );
  }
  if (sourceType === "custom") {
    return (
      <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-full bg-amber-100 text-amber-800" data-testid="vp-source-badge">
        <Pencil className="h-3 w-3" /> Custom Parameters
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-full bg-slate-100 text-slate-600" data-testid="vp-source-badge">
      <Hand className="h-3 w-3" /> Manual Input
    </span>
  );
}
